export function stripRef(raw) {
  let value = String(raw || "").trim();
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    value = value.slice(1, -1);
  }
  let depth = 0;
  for (let index = 0; index < value.length; index++) {
    const character = value[index];
    if (character === "(") depth++;
    else if (character === ")") depth--;
    else if (character === "|" && depth === 0)
      return value.slice(0, index).trim();
  }
  return value;
}

export function collectRefIdsFromDefs(defs, cfg) {
  const ids = new Set();
  for (const item of defs) {
    const raw =
      item.refScriptExpr ??
      item.refScriptId ??
      item.refScript ??
      item.ref_script_expr ??
      item.ref_script_id;
    if (!raw) continue;

    const value = stripRef(raw);
    if (/^\d+$/.test(value)) {
      ids.add(Number(value));
      continue;
    }

    const directMatch = value.match(/^\(\*(\d+)(?:@(\d+))?\*\)$/);
    if (directMatch) {
      const baseId = directMatch[1];
      const section = directMatch[2] ? Number(directMatch[2]) : null;
      if (section) {
        const id = Number(cfg[`${baseId}@${section}`]);
        if (Number.isFinite(id)) ids.add(id);
      } else {
        Object.keys(cfg)
          .filter((key) => key.startsWith(`${baseId}@`))
          .forEach((key) => {
            const id = Number(cfg[key]);
            if (Number.isFinite(id)) ids.add(id);
          });
      }
      continue;
    }

    try {
      const macro = value.match(/\(\*(\d+)\*\)/);
      const base = macro ? macro[1] : null;
      const sections = base
        ? Object.keys(cfg)
            .filter((key) => key.startsWith(`${base}@`))
            .map((key) => Number(key.split("@")[1]))
        : [null];
      for (const section of sections.length ? sections : [null]) {
        const local = { ...cfg };
        if (
          base &&
          section != null &&
          local[base] == null &&
          local[`${base}@${section}`] != null
        ) {
          local[base] = local[`${base}@${section}`];
        }
        const expression = value
          .replace(/\(\*(\d+(?:@\d+)?)\*\)/g, (_, key) =>
            JSON.stringify(local[key] ?? 0)
          )
          .replace(/\b(width|height|depth)\b/g, (_, key) =>
            String(local[key] ?? 0)
          );
        const result = Number(new Function(`return (${expression});`)());
        if (Number.isFinite(result)) ids.add(result);
      }
    } catch {
      (value.match(/\b\d+\b/g) || []).forEach((id) => ids.add(Number(id)));
    }
  }
  return ids;
}

export function parseRoom(roomString) {
  if (!roomString) return { h: 2500, w: 5000, d: 5000 };
  const [height, width, depth] = roomString
    .slice(1, -1)
    .split(",")
    .map((value) => parseFloat(value) || 0);
  return { h: height, w: width, d: depth };
}

export function normalizeItemPayload(payload) {
  const toArrayString = (value) => {
    if (value == null) return null;
    if (Array.isArray(value)) return JSON.stringify(value);
    return String(value);
  };
  const allowed = new Set([
    "itemId",
    "name",
    "details",
    "material",
    "kant",
    "qty",
    "position",
    "rotation",
    "size",
    "tablePosition",
    "tableRotation",
    "rotable",
    "color",
    "object",
    "work",
    "groupId",
    "milling",
    "millingId"
  ]);
  const normalized = Object.fromEntries(
    Object.entries(payload).filter(([key]) => allowed.has(key))
  );
  delete normalized.sizeArr;
  delete normalized.cantType;
  delete normalized.longCant;
  delete normalized.shortCant;
  if ("size" in normalized) normalized.size = toArrayString(normalized.size);
  if ("position" in normalized)
    normalized.position = toArrayString(normalized.position);
  if ("rotation" in normalized)
    normalized.rotation = toArrayString(normalized.rotation);
  if ("tablePosition" in normalized)
    normalized.tablePosition = toArrayString(normalized.tablePosition);
  if ("tableRotation" in normalized)
    normalized.tableRotation = toArrayString(normalized.tableRotation);
  if ("millingId" in normalized) {
    normalized.milling =
      normalized.millingId != null ? { millingId: normalized.millingId } : null;
    delete normalized.millingId;
  }
  if (normalized.color && normalized.color.colorId == null)
    normalized.color = null;
  return normalized;
}

export function isDTag(value) {
  return /^D\d+$/i.test(String(value || "").trim());
}

export function assignDuplungDetails(allItems, unsavedItems) {
  let max = 0;
  for (const item of allItems) {
    const match = String(item.details || "")
      .trim()
      .match(/^D(\d+)$/i);
    if (match) {
      const number = parseInt(match[1], 10);
      if (Number.isFinite(number)) max = Math.max(max, number);
    }
  }
  const labelByGroup = new Map();
  return unsavedItems.map((item) => {
    const details = String(item.details || "").trim();
    const token =
      item.__duplungGroup || (details.startsWith("DU:") ? details : null);
    if (!token || isDTag(details)) return item;
    if (!labelByGroup.has(token)) labelByGroup.set(token, `D${++max}`);
    return { ...item, details: labelByGroup.get(token) };
  });
}

export function parseSetting(value) {
  return (value || "").split(",").reduce((result, part) => {
    const [key, rawValue] = part.split(":");
    if (key && rawValue) result[key.trim()] = +rawValue;
    return result;
  }, {});
}
